import {
  Box,
  Typography,
  TextField,
  IconButton,
  Tooltip,
  useTheme,
  Chip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import EditIcon from "@mui/icons-material/Edit";
import { AppDispatch, RootState } from "../../stores";
import {
  updateSubscriptionAction,
  getBillingDetailsByUserId,
} from "../../features/subscription-module/subscriptionAction";
import { getAuthenticatedUser } from "../../features/auth-module/authAction";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { useMediaQuery } from "@mui/material";

interface Plan {
  id: string;
  planName: string;
  price: number;
  duration: string;
  description: string;
  features: string[];
  fleetEstimatorReportLimit: number;
  siteEstimatorReportLimit: number;
  isPopular?: boolean;
  isFleetEstimator?: boolean;
  isSiteEstimator?: boolean;
  mode: "public" | "private";
  userId: string | null;
}

const PlanSummary = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  backgroundColor: "#f2efe9",
  padding: theme.spacing(2),
  borderRadius: 8,
  marginBottom: theme.spacing(2),
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
    alignItems: "flex-start",
    gap: theme.spacing(1),
    padding: theme.spacing(1.5),
  },
}));

const EstimatorChip = styled(Chip)(({ theme }) => ({
  backgroundColor: "#e3f2fd",
  color: "#1976d2",
  fontWeight: 500,
  fontSize: "0.7rem",
  height: 22,
  marginRight: theme.spacing(0.5),
  marginBottom: theme.spacing(0.5),
  "& .MuiChip-label": {
    padding: theme.spacing(0, 1),
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  width: "100%",
  "& .MuiOutlinedInput-root": {
    borderRadius: "5px",
    backgroundColor: "#f5f5f5",
    "& fieldset": {
      borderColor: "#e0e0e0",
    },
    "&:hover fieldset": {
      borderColor: "#bdbdbd",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#3f51b5",
    },
  },
  "& .MuiInputLabel-root": {
    color: "#757575",
    [theme.breakpoints.down("sm")]: {
      fontSize: "0.8rem",
    },
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "#3f51b5",
  },
  "& .MuiOutlinedInput-input": {
    padding: "10px 12px",
    fontSize: "0.85rem",
    [theme.breakpoints.down("sm")]: {
      padding: "10px 12px",
      fontSize: "0.8rem",
    },
  },
}));

interface PaymentFormProps {
  selectedPlan: Plan;
  onClose: () => void;
}

const PaymentForm = ({ selectedPlan, onClose }: PaymentFormProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();
  const { billingDetails, billingDetailLoading, billingDetailError } =
    useSelector((state: RootState) => state.subscription);
  const [email, setEmail] = useState("");
  const [isEmailEditable, setIsEmailEditable] = useState(false);
  const [isCardEditable, setIsCardEditable] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const userData = JSON.parse(localStorage.getItem("user") || "{}");
  const userEmail = userData?.email || "";
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    dispatch(getBillingDetailsByUserId() as any);
  }, [dispatch]);

  const details =
    billingDetails && billingDetails.length > 0 ? billingDetails[0] : null;

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setError(null);

    try {
      let paymentMethodId = details?.paymentMethod || "";
      let paymentType = details?.paymentMethod || "";
      let cardNumber = details?.cardNumber || "";

      if (isCardEditable) {
        const cardElement = elements.getElement(CardElement);

        if (!cardElement) {
          throw new Error("Card element not found");
        }

        const { paymentMethod, error: pmError } =
          await stripe.createPaymentMethod({
            type: "card",
            card: cardElement,
            billing_details: {
              email: email || userEmail,
            },
          });

        if (pmError) {
          throw new Error(pmError.message);
        }

        paymentMethodId = paymentMethod.id;
        paymentType = paymentMethod.type;
        cardNumber = paymentMethod?.card?.last4 || "";
      }

      const result = await dispatch(
        updateSubscriptionAction({
          planId: selectedPlan.id,
          paymentMethodId,
          paymentType,
          cardNumber,
        }) as any
      );

      if (updateSubscriptionAction.fulfilled.match(result)) {
        const responseData = result.payload?.data;
        if (responseData && responseData.clientSecret) {
          const { error: confirmError, paymentIntent } =
            await stripe.confirmCardPayment(responseData.clientSecret);

          if (confirmError) {
            setError(confirmError.message || "Payment confirmation failed");
            toast.error(confirmError.message || "Payment confirmation failed");
            return;
          } else if (paymentIntent?.status === "succeeded") {
            toast.success("Payment confirmed successfully");
          } else {
            throw new Error("Payment confirmation did not succeed");
          }
        }

        const profileResult = await dispatch(getAuthenticatedUser()).unwrap();

        if (profileResult.success) {
          const profileUser = profileResult.data?.user;
          if (profileUser?.subscription?.id) {
            setSuccess(true);
            setTimeout(() => {
              onClose();
              navigate("/main/plans");
            }, 2000);
          } else {
            throw new Error("Subscription not found after update");
          }
        } else {
          throw new Error(
            profileResult.message || "Failed to fetch user profile"
          );
        }
      } else {
        throw new Error(
          (result.payload as string) || "Subscription update failed"
        );
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : "An error occurred while updating your subscription.";
      setError(errorMessage);
      toast.error(errorMessage);
    }
  };

  // Helper function to render estimator chips
  const renderEstimatorChips = () => {
    const chips = [];

    if (selectedPlan.isFleetEstimator) {
      chips.push(
        <EstimatorChip key="fleet" label="Fleet Estimator" size="small" />
      );
    }

    if (selectedPlan.isSiteEstimator) {
      chips.push(
        <EstimatorChip key="site" label="Site Estimator" size="small" />
      );
    }

    return chips;
  };

  return (
    <form id="payment-form" onSubmit={handleSubmit}>
      <PlanSummary>
        <Box>
          <Typography variant="h6" fontWeight={600} color="#4b5b5d">
            {selectedPlan.planName}
          </Typography>
          <Typography variant="body2" color="#757575" sx={{ mb: 1 }}>
            {selectedPlan.description}
          </Typography>
          {/* Render estimator chips */}
          <Box sx={{ display: "flex", flexWrap: "wrap" }}>
            {renderEstimatorChips()}
          </Box>
        </Box>
        <Typography variant="h6" fontWeight={700} color="#4b5b5d">
          ${selectedPlan.price}/
          {selectedPlan.duration === "monthly" ? "month" : "year"}
        </Typography>
      </PlanSummary>

      <Box sx={{ position: "relative" }}>
        <StyledTextField
          label="Email Address"
          type="email"
          value={email || userEmail}
          onChange={(e) => setEmail(e.target.value)}
          fullWidth
          required
          margin="normal"
          disabled={!isEmailEditable && !!userEmail}
          size={isMobile ? "small" : "medium"}
        />
        {!!userEmail && (
          <Tooltip title={isEmailEditable ? "Lock Email" : "Edit Email"}>
            <IconButton
              onClick={() => setIsEmailEditable(!isEmailEditable)}
              sx={{
                position: "absolute",
                right: 8,
                top: "50%",
                transform: "translateY(-50%)",
                color: isEmailEditable ? "#4caf50" : "#455354",
              }}
            >
              <EditIcon fontSize={isMobile ? "small" : "medium"} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <StyledTextField
        label="Payment Method"
        value={
          billingDetailLoading
            ? "Loading..."
            : details
            ? details.paymentMethod.charAt(0).toUpperCase() +
              details.paymentMethod.slice(1)
            : "N/A"
        }
        fullWidth
        margin="normal"
        InputProps={{ readOnly: true }}
        disabled
        size={isMobile ? "small" : "medium"}
      />

      <Box sx={{ position: "relative" }}>
        {isCardEditable ? (
          <Box
            sx={{
              p: 2,
              border: "1px solid #e1e5e9",
              borderRadius: 2,
              backgroundColor: "#f8f8f8",
              mt: 2,
            }}
          >
            <Typography
              variant="subtitle2"
              fontWeight={600}
              mb={1}
              color="#455354"
            >
              Card Details
            </Typography>
            <CardElement
              options={{
                style: {
                  base: {
                    fontSize: isMobile ? "14px" : "16px",
                    color: "#455354",
                    "::placeholder": {
                      color: "#aab7c4",
                    },
                    backgroundColor: "#f8f8f8",
                  },
                  invalid: {
                    color: "#9e2146",
                  },
                },
              }}
            />
          </Box>
        ) : (
          <StyledTextField
            label="Card Number"
            value={
              billingDetailLoading
                ? "Loading..."
                : details
                ? details.cardNumber || "N/A"
                : "N/A"
            }
            fullWidth
            margin="normal"
            InputProps={{ readOnly: true }}
            disabled
            size={isMobile ? "small" : "medium"}
          />
        )}
        {details?.cardNumber && (
          <Tooltip title={isCardEditable ? "Lock Card" : "Edit Card"}>
            <IconButton
              onClick={() => setIsCardEditable(!isCardEditable)}
              sx={{
                position: "absolute",
                right: 8,
                top: isCardEditable ? "50%" : "50%",
                transform: isCardEditable
                  ? "translateY(-110%)"
                  : "translateY(-50%)",
                color: isCardEditable ? "#4caf50" : "#455354",
              }}
            >
              <EditIcon fontSize={isMobile ? "small" : "medium"} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {error && (
        <Typography
          color="error"
          variant="body2"
          sx={{
            mt: 2,
            textAlign: "center",
            bgcolor: "#ffebee",
            p: 1,
            borderRadius: 4,
            fontWeight: 600,
            fontSize: isMobile ? 14 : 16,
          }}
        >
          {error}
        </Typography>
      )}

      {billingDetailError && (
        <Typography
          color="error"
          variant="body2"
          sx={{
            mt: 2,
            textAlign: "center",
            bgcolor: "#ffebee",
            p: 1,
            borderRadius: 4,
            fontWeight: 600,
            fontSize: isMobile ? 14 : 16,
          }}
        >
          {billingDetailError}
        </Typography>
      )}

      {success && (
        <Typography
          color="success.main"
          variant="body2"
          sx={{
            mt: 2,
            textAlign: "center",
            bgcolor: "#e8f5e9",
            p: 1,
            borderRadius: 4,
            fontWeight: 600,
            fontSize: isMobile ? 14 : 16,
          }}
        >
          Subscription updated successfully! 🎉
        </Typography>
      )}
    </form>
  );
};

export default PaymentForm;
